/**
 * The primitives, as typed wrappers over the design language's own classes.
 *
 * They deliberately do NOT restyle anything: `shared/styles/base.css` is copied verbatim from
 * `shared/styles/`, so a component that invented its own padding would be the drift the
 * copy exists to prevent. What these add is the part a class name cannot carry — that a
 * verdict is one of exactly five, that a meter's colour is a function of its fill, and that a
 * gate is only ever rendered after a result.
 */

import type { AnchorHTMLAttributes, ReactNode } from "react";
import { trackCta } from "./events";
import { type Verdict, VERDICT_MEANING, asVerdict } from "./verdict";

export function VerdictChip({
  value,
  dim = false,
}: {
  value: string | null | undefined;
  dim?: boolean;
}) {
  const v: Verdict = asVerdict(value);
  return (
    <span className={`v v-${v}${dim ? " dim" : ""}`} title={VERDICT_MEANING[v]}>
      {v}
    </span>
  );
}

/**
 * A usage meter. Colour is DEVIATION, never health — a bar that turns green at 20% tells an
 * operator that being under budget is an achievement, and then the amber one stops meaning
 * anything. Graphite until it is worth looking at.
 */
export function Meter({ used, limit }: { used: number; limit: number | null }) {
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const level = pct >= 100 ? "over" : pct >= 80 ? "warn" : "";
  return (
    <div className="meter" role="meter" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <i className={level} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Card({
  children,
  pad = true,
  className = "",
}: {
  children: ReactNode;
  pad?: boolean | "lg";
  className?: string;
}) {
  const p = pad === "lg" ? " pad-lg" : pad ? " pad" : "";
  return <div className={`card${p} ${className}`.trim()}>{children}</div>;
}

export function Head({ title, lede, pill }: { title: string; lede?: string; pill?: string }) {
  return (
    <div className="tool-head">
      {pill && (
        <span className="pill">
          <span className="dot" />
          {pill}
        </span>
      )}
      <h1>{title}</h1>
      {lede && <p className="lede">{lede}</p>}
    </div>
  );
}

export function Rule({ label }: { label: string }) {
  return (
    <div className="rule">
      <span className="label">{label}</span>
    </div>
  );
}

/** A wide table that scrolls inside its own box. The page body must never scroll sideways —
 *  `audit.mjs` measures exactly this at 390px, and it is the check the design language
 *  asserted before anyone had measured it. */
export function ScrollX({ children }: { children: ReactNode }) {
  return <div className="scroll-x">{children}</div>;
}

/**
 * A state a data view is in, said plainly.
 *
 * Every one of these is a different sentence, on purpose. An empty panel is indistinguishable
 * from a broken one, and "no results" where the real answer is "we are not the ones who keep
 * that" answers the question WRONGLY rather than not at all.
 */
export function State({
  kind,
  title,
  detail,
  action,
}: {
  kind: "loading" | "empty" | "error";
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className={`state state-${kind}`} data-testid={`state-${kind}`}>
      {kind === "loading" && <div className="spin" aria-hidden="true" />}
      <p className="md">{title}</p>
      {detail && <p className="sm muted">{detail}</p>}
      {action}
    </div>
  );
}

export function CodeBlock({ children }: { children: ReactNode }) {
  return <pre className="code">{children}</pre>;
}

export function Stat({ n, t }: { n: string; t: string }) {
  return (
    <div className="stat">
      <div className="n">{n}</div>
      <div className="t">{t}</div>
    </div>
  );
}

/**
 * A call to action that records that it was clicked.
 *
 * WHY A COMPONENT AND NOT AN `onClick` ON EACH LINK. "Get a key" appears in the nav, the hero,
 * the pricing table, the four-outcomes block and the footer. They are the same words and they
 * are not the same button — knowing which one converts is the difference between moving a
 * button and guessing about it. Hand-written handlers get that `placement` field right the day
 * they are written and wrong forever after, because the next person copies the markup.
 *
 * `cta` is what it offers; `placement` is where it was. Both are required, so a copied element
 * cannot silently inherit somebody else's attribution — the compiler asks.
 *
 * It renders an ordinary anchor. Analytics that swallows a navigation is worse than no
 * analytics, so nothing here calls `preventDefault`, and a middle-click or a keyboard activation
 * behaves exactly as it would without this wrapper.
 */
export function Cta({
  href,
  cta,
  placement,
  className = "btn",
  children,
  ...rest
}: {
  href: string;
  cta: string;
  placement: string;
  className?: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "className">) {
  return (
    <a
      href={href}
      className={className}
      onClick={() => trackCta(cta, placement, href)}
      {...rest}
    >
      {children}
    </a>
  );
}
